import React, { useState } from 'react'
import {
  Brain, ChevronRight, CheckCircle, XCircle,
  RotateCcw, MessageCircle, Target,
  AlertTriangle, BookOpen, Sparkles, ChevronDown, ChevronUp
} from 'lucide-react'
import { useUnitProgress } from '../store/progressStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'A1-1'

interface TutorScenario {
  id: string
  label: string
  emoji: string
  category: string
  learnStepId: string
  question: string
  correctAnswer: string
  wrongAnswer: string
  misconceptionCode: string
  note: string
  analysis: {
    diagnosis: string
    rootCause: string
    correction: string
    example: string
  }
  followUps: Array<{ q: string; a: string }>
}

const DEMO_SCENARIOS: TutorScenario[] = [
  {
    id: 'S1',
    label: '시나리오 1',
    emoji: '🔤',
    category: '기호 생략',
    learnStepId: 'AA01',
    question: 'a×3을 기호를 생략하여 나타내면?',
    correctAnswer: '3a (숫자가 문자 앞)',
    wrongAnswer: 'a3',
    misconceptionCode: 'AM01',
    note: '숫자는 항상 문자 앞에 써야 하는데 순서를 혼동',
    analysis: {
      diagnosis: '학생이 a×3을 a3으로 표현했습니다. 숫자와 문자의 순서를 혼동했습니다.',
      rootCause: '수학에서는 수×문자일 때 수를 앞에 씁니다. a3은 관례상 쓰지 않는 표현입니다.',
      correction: '규칙: 수와 문자의 곱에서 수를 앞에!\na×3 = 3×a = 3a\n(-2)×x = -2x\n0.5×y = 0.5y',
      example: 'a×3 → 3a ✓\nx×(-5) → -5x ✓\n3×b×2 → 먼저 수끼리: 6b ✓',
    },
    followUps: [
      { q: 'x×(-1)은 어떻게 쓰나요?', a: '-x (계수 -1에서 1도 생략!)' },
      { q: '2×a×b를 간단히 하면?', a: '2ab (알파벳 순서, × 모두 생략)' },
    ],
  },
  {
    id: 'S2',
    label: '시나리오 2',
    emoji: '🧮',
    category: '음수 대입',
    learnStepId: 'AA03',
    question: 'x=-3일 때 x²의 값은?',
    correctAnswer: '9 ((-3)²=9)',
    wrongAnswer: '-9 (-3²=-9로 계산)',
    misconceptionCode: 'AM05',
    note: '음수를 대입할 때 괄호를 생략해서 x²=-3²=-9로 계산하는 오류',
    analysis: {
      diagnosis: '학생이 x=-3을 대입할 때 (-3)²이 아닌 -3²=-9로 계산했습니다.',
      rootCause: '-3²은 -(3²)=-9이지만, x=-3일 때 x²은 (-3)²=9입니다. 대입 시 괄호로 감싸야 합니다.',
      correction: '음수 대입 규칙: 반드시 괄호로!\nx=-3 → x² = (-3)² = 9\n\n-3²=-9 (마이너스가 제곱 밖)\n(-3)²=9 (마이너스가 제곱 안)',
      example: 'a=-2일 때:\na² = (-2)² = 4\n2a = 2×(-2) = -4\na²+2a = 4+(-4) = 0',
    },
    followUps: [
      { q: 'x=-1일 때 3x²-x의 값은?', a: '3×(-1)²-(-1) = 3×1+1 = 4' },
      { q: '왜 괄호가 없으면 다른 값이 나오나요?', a: '-3²은 -(3²)=-9. 제곱이 먼저 계산되고 음수 부호가 나중에 붙습니다.' },
    ],
  },
  {
    id: 'S3',
    label: '시나리오 3',
    emoji: '📦',
    category: '동류항',
    learnStepId: 'AA04',
    question: '3x+2y+x-y를 간단히 하면?',
    correctAnswer: '4x+y',
    wrongAnswer: '6xy',
    misconceptionCode: 'AM07',
    note: '서로 다른 문자 항을 곱셈처럼 합쳐버리는 오류',
    analysis: {
      diagnosis: '학생이 3x+2y+x-y를 6xy로 계산했습니다. 덧셈인데 곱셈처럼 처리한 오류입니다.',
      rootCause: '동류항은 같은 문자끼리만 더할 수 있습니다. x항과 y항은 서로 다른 문자이므로 합칠 수 없습니다.',
      correction: '동류항 정리 방법:\n① x항끼리: 3x+x = 4x\n② y항끼리: 2y-y = y\n③ 결합: 4x+y',
      example: '3x+2y+x-y\n= (3x+x) + (2y-y)\n= 4x + y',
    },
    followUps: [
      { q: '2x+3y-x+4는 어떻게 정리하나요?', a: 'x항: 2x-x=x, y항: 3y, 상수: 4 → x+3y+4' },
      { q: '3x²+2x는 동류항인가요?', a: '아니요! 차수가 다릅니다. x²와 x¹은 동류항이 아닙니다.' },
    ],
  },
  {
    id: 'S4',
    label: '시나리오 4',
    emoji: '➕',
    category: '뺄셈 부호',
    learnStepId: 'AA05',
    question: '(4x+3)-(2x-5)를 계산하면?',
    correctAnswer: '2x+8',
    wrongAnswer: '2x-2',
    misconceptionCode: 'AM09',
    note: '-(2x-5)에서 -5의 부호를 반전하지 않고 그대로 사용',
    analysis: {
      diagnosis: '학생이 (4x+3)-(2x-5)=2x-2로 계산했습니다. -(2x-5)를 -2x-5로 처리한 오류입니다.',
      rootCause: '빼기 뒤 괄호를 풀 때 안의 모든 부호가 반전됩니다. -(2x-5) = -2x+5.',
      correction: '-(괄호) 부호 반전 규칙:\n-(2x-5) = -2x+5 (- → +)\n-(3a+2) = -3a-2 (+ → -)\n\n(4x+3)-(2x-5)\n= 4x+3-2x+5\n= 2x+8',
      example: '(3x-1)-(x+4)\n= 3x-1-x-4\n= 2x-5',
    },
    followUps: [
      { q: '-(x-3)을 전개하면?', a: '-x+3 (마이너스를 분배: -1×x=-x, -1×(-3)=+3)' },
      { q: '(5a-2)-(3a-2)를 계산하면?', a: '5a-2-3a+2 = 2a' },
    ],
  },
  {
    id: 'S5',
    label: '시나리오 5',
    emoji: '✖️',
    category: '분배법칙',
    learnStepId: 'AA06',
    question: '3(2x-4)를 계산하면?',
    correctAnswer: '6x-12',
    wrongAnswer: '6x-4',
    misconceptionCode: 'AM10',
    note: '분배법칙에서 상수항에 곱하는 것을 빠뜨리는 오류',
    analysis: {
      diagnosis: '학생이 3(2x-4)=6x-4로 계산했습니다. 3을 2x에만 곱하고 -4에는 곱하지 않은 오류입니다.',
      rootCause: '분배법칙: a(b+c) = ab+ac. 괄호 안의 모든 항에 빠짐없이 곱해야 합니다.',
      correction: '분배법칙 체크:\n3(2x-4)\n= 3×2x + 3×(-4)\n= 6x + (-12)\n= 6x-12\n\n모든 항에 ✓ 표시하며 확인하는 습관!',
      example: '-2(x+3) = -2x-6\n½(4x-2) = 2x-1\n(3x-6)÷3 = x-2',
    },
    followUps: [
      { q: '-4(x-2)를 계산하면?', a: '-4x+8 (-4×x=-4x, -4×(-2)=+8)' },
      { q: '2(x+1)+3(x-2)를 계산하면?', a: '2x+2+3x-6 = 5x-4' },
    ],
  },
]

// ── 서브 컴포넌트 ────────────────────────────────────────────────────────────
function FragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const visible = fragments.slice(0, 2)
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50 mb-4">
      <p className="text-xs font-bold text-amber-700 mb-2">⚡ AI 예상 취약 개념</p>
      <div className="space-y-1.5">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-center gap-2 p-2 rounded-lg border ${cfg.color}`}>
              <span>{f.fragment.emoji}</span>
              <span className={`text-xs font-semibold flex-1 ${cfg.color}`}>{f.fragment.title}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function FollowUpCard({ followUps }: { followUps: TutorScenario['followUps'] }) {
  const [openIdx, setOpenIdx] = useState<number | null>(null)
  return (
    <div className="mt-3 space-y-2">
      <p className="text-xs font-bold text-violet-600 mb-1">💬 추가 질문</p>
      {followUps.map((fu, i) => (
        <div key={i} className="border border-violet-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setOpenIdx(openIdx === i ? null : i)}
            className="w-full flex items-center justify-between px-3 py-2 bg-violet-50 hover:bg-violet-100 text-left transition-colors"
          >
            <span className="text-xs text-slate-700 font-medium flex-1">{fu.q}</span>
            {openIdx === i
              ? <ChevronUp size={13} className="text-slate-400 flex-shrink-0" />
              : <ChevronDown size={13} className="text-slate-400 flex-shrink-0" />}
          </button>
          {openIdx === i && (
            <div className="px-3 py-2 bg-white">
              <p className="text-xs text-violet-700 font-semibold whitespace-pre-line">{fu.a}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ── 메인 컴포넌트 ────────────────────────────────────────────────────────────
export default function A1TutorPage() {
  const [selectedId, setSelectedId] = useState<string>('S1')
  const [step, setStep] = useState<'question' | 'wrong' | 'analysis' | 'correct'>('question')
  const [showNote, setShowNote] = useState(false)

  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const scenario = DEMO_SCENARIOS.find(s => s.id === selectedId)!

  const reset = () => { setStep('question'); setShowNote(false) }
  const handleSelect = (id: string) => { setSelectedId(id); reset() }

  return (
    <div className="p-4 space-y-4">
      <FragmentPanel fragments={predictedFragments} />

      {/* 헤더 */}
      <div className="card bg-gradient-to-r from-violet-50 to-indigo-50 border border-violet-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-100 rounded-xl flex items-center justify-center">
            <Brain size={20} className="text-violet-600" />
          </div>
          <div>
            <h2 className="text-base font-black text-slate-800">AI 튜터 — 문자의 사용과 식</h2>
            <p className="text-xs text-slate-500">A1-1 오개념 분석 · 맞춤 피드백</p>
          </div>
        </div>
      </div>

      {/* 시나리오 선택 */}
      <div>
        <p className="text-xs font-bold text-slate-500 mb-2 px-1">📋 학습 시나리오 선택</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {DEMO_SCENARIOS.map(s => (
            <button key={s.id} onClick={() => handleSelect(s.id)}
              className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                selectedId === s.id
                  ? 'bg-violet-500 text-white border-violet-500 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-violet-50 hover:border-violet-200'
              }`}>
              {s.emoji} {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 시나리오 카드 */}
      <div className="card border-2 border-violet-200">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full font-semibold">
            {scenario.category}
          </span>
          <span className="text-xs text-slate-400">{scenario.learnStepId}</span>
        </div>

        {step === 'question' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1 flex items-center gap-1">
                <MessageCircle size={11} /> 학생 질문
              </p>
              <p className="text-sm font-semibold text-slate-800">{scenario.question}</p>
            </div>
            <p className="text-xs text-slate-500 text-center">학생의 답을 먼저 예상해보세요.</p>
            <button onClick={() => setStep('wrong')}
              className="w-full py-2.5 bg-violet-500 hover:bg-violet-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <ChevronRight size={15} /> 학생 답 확인
            </button>
          </div>
        )}

        {step === 'wrong' && (
          <div className="space-y-3">
            <div className="bg-slate-50 rounded-xl p-3 border border-slate-200">
              <p className="text-xs font-bold text-slate-500 mb-1">📝 문제</p>
              <p className="text-sm text-slate-700">{scenario.question}</p>
            </div>
            <div className="bg-red-50 rounded-xl p-3 border border-red-200">
              <p className="text-xs font-bold text-red-500 mb-1 flex items-center gap-1">
                <XCircle size={11} /> 학생 오답
              </p>
              <p className="text-sm font-bold text-red-700">{scenario.wrongAnswer}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowNote(!showNote)}
                className="flex-1 py-2 bg-amber-100 text-amber-700 rounded-xl text-xs font-semibold hover:bg-amber-200 transition-colors flex items-center justify-center gap-1">
                <AlertTriangle size={12} /> 오개념 메모
              </button>
              <button onClick={() => setStep('analysis')}
                className="flex-1 py-2 bg-violet-500 text-white rounded-xl text-xs font-bold hover:bg-violet-600 transition-colors flex items-center justify-center gap-1">
                <Brain size={12} /> AI 분석
              </button>
            </div>
            {showNote && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                <p className="text-xs font-bold text-amber-700 mb-1">⚠️ 오개념 [{scenario.misconceptionCode}]</p>
                <p className="text-xs text-amber-800">{scenario.note}</p>
              </div>
            )}
          </div>
        )}

        {step === 'analysis' && (
          <div className="space-y-3">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-3">
              <p className="text-xs font-bold text-violet-600 mb-2 flex items-center gap-1">
                <Sparkles size={11} /> AI 진단
              </p>
              <p className="text-xs text-violet-800">{scenario.analysis.diagnosis}</p>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
              <p className="text-xs font-bold text-orange-600 mb-1">🔍 근본 원인</p>
              <p className="text-xs text-orange-800">{scenario.analysis.rootCause}</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
              <p className="text-xs font-bold text-blue-600 mb-1">🛠️ 교정 방법</p>
              <p className="text-xs text-blue-800 whitespace-pre-line">{scenario.analysis.correction}</p>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3">
              <p className="text-xs font-bold text-slate-600 mb-1">📌 예시</p>
              <p className="text-xs text-slate-700 font-mono whitespace-pre-line">{scenario.analysis.example}</p>
            </div>
            <button onClick={() => setStep('correct')}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl text-sm transition-colors flex items-center justify-center gap-2">
              <CheckCircle size={15} /> 정답 및 후속 학습
            </button>
          </div>
        )}

        {step === 'correct' && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
              <p className="text-xs font-bold text-emerald-600 mb-1 flex items-center gap-1">
                <CheckCircle size={11} /> 정답
              </p>
              <p className="text-sm font-bold text-emerald-700 whitespace-pre-line">{scenario.correctAnswer}</p>
            </div>
            <FollowUpCard followUps={scenario.followUps} />
            <div className="flex gap-2 mt-2">
              <button onClick={reset}
                className="flex-1 py-2 border border-slate-200 rounded-xl text-xs text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1">
                <RotateCcw size={12} /> 다시 보기
              </button>
              <button onClick={() => {
                const keys = DEMO_SCENARIOS.map(s => s.id)
                const next = keys[(keys.indexOf(selectedId) + 1) % keys.length]
                handleSelect(next)
              }}
                className="flex-1 py-2 bg-violet-500 text-white rounded-xl text-xs font-bold hover:bg-violet-600 flex items-center justify-center gap-1">
                <ChevronRight size={12} /> 다음 시나리오
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 진행 표시 */}
      <div className="flex items-center justify-center gap-2">
        {(['question', 'wrong', 'analysis', 'correct'] as const).map(s => (
          <div key={s} className={`h-1.5 rounded-full transition-all ${s === step ? 'w-8 bg-violet-500' : 'w-3 bg-slate-200'}`} />
        ))}
      </div>

      {/* 핵심 오개념 정리 */}
      <div className="card border border-slate-200">
        <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
          <Target size={14} className="text-violet-500" /> 핵심 오개념 정리
        </h3>
        <div className="space-y-2">
          {[
            { code: 'AM01', text: '숫자는 문자 앞 — a×3 = 3a (a3 아님)' },
            { code: 'AM03', text: '계수 1 생략 — 1×x = x (1x 아님)' },
            { code: 'AM05', text: '음수 대입 시 괄호 필수 — x=-2 → x²=(-2)²=4' },
            { code: 'AM07', text: '다른 문자 항은 합칠 수 없음 — 3x+2y ≠ 5xy' },
            { code: 'AM09', text: '-(괄호) 부호 전체 반전 — -(x-3)=-x+3' },
            { code: 'AM10', text: '분배 시 모든 항에 곱하기 — 3(x-4)=3x-12' },
          ].map(item => (
            <div key={item.code} className="flex gap-2 items-start bg-red-50 border border-red-100 rounded-xl px-3 py-2">
              <span className="text-xs font-black text-red-400 flex-shrink-0 mt-0.5">{item.code}</span>
              <p className="text-xs text-red-800">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* 학습 링크 */}
      <div className="card border border-violet-100 bg-violet-50">
        <p className="text-xs font-bold text-violet-700 mb-2 flex items-center gap-1">
          <BookOpen size={12} /> 관련 학습 단계
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: 'AA01', title: '문자를 사용한 식' },
            { id: 'AA02', title: '기호 생략 규칙' },
            { id: 'AA03', title: '식의 값 구하기' },
            { id: 'AA04', title: '동류항 정리' },
            { id: 'AA05', title: '덧셈·뺄셈' },
            { id: 'AA06', title: '곱셈·나눗셈' },
          ].map(s => (
            <div key={s.id} className="bg-white rounded-xl px-3 py-2 border border-violet-100">
              <p className="text-xs font-bold text-violet-600">{s.id}</p>
              <p className="text-xs text-slate-600">{s.title}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
